import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';

const EFFECTIVE_DATE = 'October 30, 2026';

// Every outside service the site itself loads or sends data to. Keep in sync
// with what index.html, ContactForm.js and server/ actually use.
const providers = [
  {
    name: 'Google Analytics',
    purpose: 'Visit statistics (which pages are read, how visitors arrive). Advertising features are turned off.',
    url: 'https://policies.google.com/privacy'
  },
  {
    name: 'Cloudflare',
    purpose: 'Delivers the site securely, blocks malicious traffic, and runs the Turnstile check that keeps spam out of the contact form.',
    url: 'https://www.cloudflare.com/privacypolicy/'
  },
  {
    name: 'Twilio SendGrid',
    purpose: 'Delivers contact-form messages to our staff by email.',
    url: 'https://www.twilio.com/en-us/legal/privacy'
  }
];

function PrivacyPolicy() {
  return (
    <div className="privacy-page">
      <Seo
        title="Website Privacy Policy"
        path="/privacy-policy"
        description="How the Boca-Delray Pathology Laboratories website handles visitor information: what the contact form and analytics collect, the cookies used, and your choices."
      />
      <section className="page-banner">
        <p className="section-label">Legal</p>
        <h1>Website Privacy Policy</h1>
        <p>{`Effective ${EFFECTIVE_DATE}`}</p>
      </section>

      <div className="policy-body">
        <section>
          <h2>The Short Version</h2>
          <ul>
            <li>The contact form is the only place this website asks for personal information, and we use it only to reply to you.</li>
            <li>We use Google Analytics to count visits. Its advertising features are off, and it does not run at all if your browser sends a Global Privacy Control signal.</li>
            <li>We do not run ads, advertising or social media tracking pixels, or session recording, and we do not sell or share your information.</li>
            <li>Never send patient information through this website.</li>
          </ul>
        </section>

        <section>
          <h2>What This Policy Covers</h2>
          <p>This policy covers the public website at bocadelraypath.com, operated by Boca-Delray Pathology Laboratories in Delray Beach, Florida.</p>
          <p>The <a href="https://bdp.labdynamo.com" target="_blank" rel="noopener noreferrer">provider portal</a> used by referring practices to order tests and view reports is a separate, secured system and is not covered by this policy.</p>
        </section>

        <section>
          <h2>What We Collect, and Why</h2>
          <h3>Information you send us</h3>
          <p>When you use the <Link to="/contact">contact form</Link>, we collect your name, email address, organization, the type of inquiry, and your message. It is emailed to our staff and used only to respond to you. We do not add you to a mailing list.</p>
          <p>Please do not include patient names, dates of birth, specimen IDs, or any other protected health information in the form. For anything about a patient or a case, use the provider portal or call the laboratory.</p>

          <h3>Visit statistics</h3>
          <p>Google Analytics tells us, in aggregate, which pages are viewed, how visitors found the site, the type of device and browser used, and approximate location (city or region). It also records when someone taps a phone number, follows the provider portal link, or sends the contact form. These events record only that the action happened and the type of inquiry, never what you typed.</p>
          <p>We have turned off Google Signals and ad personalization for this site, so this data is not used to show you ads or linked to your Google account by us.</p>

          <h3>Server and security logs</h3>
          <p>Like most websites, our server and Cloudflare automatically record technical details of each request, such as IP address, page requested, and time. We use these only to keep the site secure, prevent abuse (for example, limiting repeated form submissions), and fix problems, and we keep them for a limited time.</p>
        </section>

        <section>
          <h2>What We Don&rsquo;t Do</h2>
          <ul>
            <li>No advertising, remarketing, or ad-conversion tags.</li>
            <li>No Facebook/Meta, LinkedIn, TikTok, or other social media pixels.</li>
            <li>No session recording or heatmap tools.</li>
            <li>No selling, renting, or sharing of your information for anyone else&rsquo;s marketing.</li>
            <li>No third-party font or icon services: fonts load from our own server.</li>
          </ul>
        </section>

        <section>
          <h2>Services Behind the Site</h2>
          <p>These providers handle data on our behalf, under their own privacy policies:</p>
          <div className="policy-table-wrap">
            <table className="policy-table">
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col">What it does</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <tr key={provider.name}>
                    <th scope="row">
                      <a href={provider.url} target="_blank" rel="noopener noreferrer">{provider.name}</a>
                    </th>
                    <td>{provider.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>The &ldquo;View on Google Maps&rdquo; link opens Google Maps directly; nothing from Google Maps loads on our pages unless you click it.</p>
        </section>

        <section>
          <h2>Cookies</h2>
          <p>This site sets no cookies of its own. The cookies you may see are:</p>
          <ul>
            <li><strong>Google Analytics</strong> (<code>_ga</code> and <code>_ga_MQF1Y5749L</code>): distinguish one visit from another so visits can be counted. They expire after up to two years.</li>
            <li><strong>Cloudflare security cookies</strong> (such as <code>__cf_bm</code>): set only when needed to tell real visitors from bots. They last about 30 minutes and are not used for tracking.</li>
          </ul>
          <p>Because we use no advertising or cross-site tracking cookies, we do not show a cookie banner.</p>
        </section>

        <section>
          <h2>Your Choices</h2>
          <ul>
            <li><strong>Global Privacy Control:</strong> if your browser or a privacy extension sends this signal, our pages do not load Google Analytics at all.</li>
            <li><strong>Browser settings:</strong> you can block or delete cookies in your browser; the site works without them.</li>
            <li><strong>Google&rsquo;s opt-out:</strong> the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a> stops Google Analytics on every site.</li>
            <li><strong>Your messages:</strong> if you have contacted us through the form, you may ask what we have, or ask us to correct or delete it, by calling <a href="tel:+15616387577">(561) 638-7577</a>. Some records may need to be kept where the law requires it.</li>
          </ul>
        </section>

        <section>
          <h2>Security and Children</h2>
          <p>The site is served only over encrypted HTTPS, and we collect as little as we can. No method of transmission over the internet is completely secure, which is another reason not to send health information through the website.</p>
          <p>This website is intended for healthcare professionals, practices, and adult members of the public. We do not knowingly collect information from children under 13.</p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>If our website practices change, we will update this page and its effective date.</p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>Questions about this policy or our privacy practices:</p>
          <address>
            Boca-Delray Pathology Laboratories<br />
            Attn: Privacy<br />
            5150 Linton Blvd, Suite 250<br />
            Delray Beach, FL 33484<br />
            Phone: <a href="tel:+15616387577">(561) 638-7577</a>
          </address>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
